import { changeTone, formatConfidence, formatPercent, formatPrice } from '../lib/format.js';

/** Column order is the contract published by the pipeline (`config.TARGET_HORIZONS`). */
const HORIZONS = ['1D', '1W', '1M', '3M', '6M', '1Y'];

const METHOD_LABELS = {
  'atr-band': 'ATR band',
  'regression-drift': 'Regression drift',
};

/**
 * Multi-horizon target grid (1D → 1Y).
 *
 * @param {{targets: Record<string, {target: number, low: number, high: number,
 *   upside_pct: number, confidence: number, method: string, bias: string}>,
 *   currentPrice: number}} props
 */
export default function TargetTable({ targets = {}, currentPrice }) {
  const available = HORIZONS.filter((horizon) => targets[horizon]);

  return (
    <section className="panel overflow-hidden">
      <header className="panel-heading">
        <h2 className="panel-title">Price targets</h2>
        <span className="text-[11px] text-slate-500">
          spot {formatPrice(currentPrice)}
        </span>
      </header>

      {available.length === 0 ? (
        <p className="px-4 py-6 text-sm text-slate-500">
          No targets published for this instrument yet.
        </p>
      ) : (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[640px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-terminal-700 text-left">
                <th className="px-4 py-2 font-medium text-slate-500">Horizon</th>
                <th className="px-4 py-2 text-right font-medium text-slate-500">Target</th>
                <th className="px-4 py-2 text-right font-medium text-slate-500">Upside</th>
                <th className="px-4 py-2 text-right font-medium text-slate-500">Band (low–high)</th>
                <th className="px-4 py-2 text-right font-medium text-slate-500">Confidence</th>
                <th className="px-4 py-2 text-right font-medium text-slate-500">Model</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {available.map((horizon) => {
                const entry = targets[horizon];
                return (
                  <tr
                    key={horizon}
                    className="border-b border-terminal-800 last:border-b-0 hover:bg-terminal-850/60"
                  >
                    <td className="px-4 py-3">
                      <span className="pill border-terminal-600 bg-terminal-800 text-slate-300">
                        {horizon}
                      </span>
                      <span className="ml-2 text-[11px] uppercase tracking-wide text-slate-500">
                        {entry.bias}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-100">
                      {formatPrice(entry.target)}
                    </td>
                    <td className={`px-4 py-3 text-right ${changeTone(entry.upside_pct)}`}>
                      {formatPercent(entry.upside_pct)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-400">
                      {formatPrice(entry.low)} – {formatPrice(entry.high)}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-300">
                      {formatConfidence(entry.confidence)}
                    </td>
                    <td className="px-4 py-3 text-right text-[11px] text-slate-500">
                      {METHOD_LABELS[entry.method] ?? entry.method}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
